// lib/slides/elementClipboard.ts
import type { SlideElement } from '#types/types';

// Copied elements travel as marked text on the system clipboard, so a later screenshot copy replaces
// them like anywhere else, and a bubble pastes into another deck or tab.
const MARK = 'fater-slides-elements:';
const TYPES = new Set(['note', 'arrow', 'rect', 'ellipse', 'text', 'stamp', 'image', 'block', 'patch']);
const OFFSET = 12;

export const copiedElementsText = (elements: SlideElement[]) => MARK + JSON.stringify(elements);

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** The pasted elements with fresh ids, placed on a page of `width` × `height`; null when the text
 *  is not a Slides copy. A copy pasted over its original is offset so both stay visible. */
export function pastedElements(text: string, page: { width: number; height: number; elements: SlideElement[] }, newId: () => string): SlideElement[] | null {
  if (!text.startsWith(MARK)) return null;
  let raw: unknown;
  try { raw = JSON.parse(text.slice(MARK.length)); } catch { return null; }
  if (!Array.isArray(raw)) return null;
  const valid = raw.filter((el): el is SlideElement => !!el && typeof el === 'object' && TYPES.has(el.type)
    && !!el.frame && finite(el.frame.x) && finite(el.frame.y) && finite(el.frame.w) && finite(el.frame.h));
  if (!valid.length) return null;
  const onOriginal = valid.some((el) => page.elements.some((e) => e.id === el.id));
  const shift = onOriginal ? OFFSET : 0;
  const clamp = (v: number, size: number, room: number) => Math.min(Math.max(0, v), Math.max(0, room - Math.max(0, size)));
  return valid.map((el) => {
    // A paste is the person's own placement: the agent's provenance and metering stay on the original.
    const { credits: _credits, ...rest } = el;
    const x = clamp(el.frame.x + shift, el.frame.w, page.width), y = clamp(el.frame.y + shift, el.frame.h, page.height);
    const copy = { ...rest, id: newId(), origin: 'user', frame: { ...el.frame, x, y } } as SlideElement;
    if (copy.type === 'note' && copy.anchor && finite(copy.anchor.x) && finite(copy.anchor.y)) {
      copy.anchor = { x: clamp(copy.anchor.x + x - el.frame.x, 0, page.width), y: clamp(copy.anchor.y + y - el.frame.y, 0, page.height) };
    }
    return copy;
  });
}
